import { useCallback, useEffect, useRef, useState } from 'react';
import {
  approveApproval,
  consumeChatStream,
  fetchConversation,
  generateConversationTitle,
  openAgentChatStream,
  openAiChatStream,
  rejectApproval,
  looksLikeHtmlErrorPage,
  summarizeHttpErrorBody,
  uploadChatAttachment,
  type AgentChatStreamEvent,
  type ChatMessage,
  type StoredChatMessage,
} from '../../lib/api';
import type {
  ChatUiMessage,
  MessageAttachmentRef,
  PendingAttachment,
  SecureFormFieldDef,
  ToolCallState,
} from './types';
import { parseThinkingBlocks, stripThinkingArtifacts } from './thinking';

let counter = 0;
const uid = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(counter++).toString(36)}`;

const IMAGE_TYPES = /^image\//;
/** Abort if the SSE stream goes silent this long (ms). Above MCP/tool budgets
 * (90s MCP→API, 120s API→MCP) so a single long tool call is not cut mid-flight;
 * server also emits `ping` keepalives every ~15s during tool/agent waits. */
const STREAM_IDLE_MS = 150_000;
const PROMPT_HINT = /current task domain:/i;

function failRunningTools(message: ChatUiMessage, reason: string): ChatUiMessage {
  return {
    ...message,
    toolCalls: message.toolCalls.map((t) =>
      t.status === 'running'
        ? {
            ...t,
            status: 'error' as const,
            ok: false,
            result: { ok: false, error: reason, status: 'interrupted' },
          }
        : t
    ),
  };
}

function transportErrorMessage(err: unknown, exhaustedRetries = false): string {
  const raw = err instanceof Error ? err.message : 'Network error';
  const base =
    /failed to fetch|networkerror|load failed|network request failed/i.test(raw)
      ? 'Network error'
      : raw;
  if (exhaustedRetries || /after \d+ attempts/i.test(base)) {
    return /still unavailable after/i.test(base)
      ? base
      : `${base} — still unavailable after retries. Use Resume to continue.`;
  }
  return base;
}

interface UseChatOptions {
  template?: string;
  agentMode?: boolean;
  conversationId?: string | null;
  onConversationId?: (id: string) => void;
  onConversationChanged?: () => void;
}

interface LastTurn {
  history: ChatMessage[];
  attachmentIds: string[];
  truncateAfterMessageId?: string;
}

function mergeThinkingContent(
  raw: string,
  existingThinking?: string,
  opts: { trim?: boolean } = { trim: true }
) {
  const trim = opts.trim !== false;
  if (!/<thinking>/i.test(raw) && !PROMPT_HINT.test(raw)) {
    return {
      content: stripThinkingArtifacts(raw, { trim }),
      thinking: existingThinking,
    };
  }
  const parsed = parseThinkingBlocks(raw, { trim });
  return {
    content: parsed.answer,
    thinking:
      [existingThinking, parsed.thinking].filter(Boolean).join('\n\n') || existingThinking,
  };
}

/**
 * Rebuild the visible transcript from persisted rows. Tool messages are folded
 * into the assistant turn that requested them so a reloaded chat looks the same
 * as a live one. One corrupt row must never wipe the whole transcript.
 *
 * Consecutive assistant rows from one tool-loop turn (assistant+tools, then a
 * final text reply) are coalesced so the UI does not show duplicate bubbles.
 */
function hydrateMessages(stored: StoredChatMessage[]): ChatUiMessage[] {
  const out: ChatUiMessage[] = [];
  const toolResults = new Map<string, StoredChatMessage>();

  for (const row of stored) {
    try {
      if (row.role === 'tool' && row.toolCallId) toolResults.set(row.toolCallId, row);
    } catch {
      // skip corrupt tool index entry
    }
  }

  for (const row of stored) {
    try {
      if (row.role === 'tool' || row.role === 'system') continue;

      const rawToolCalls = Array.isArray(row.toolCalls) ? row.toolCalls : [];
      const toolCalls: ToolCallState[] = rawToolCalls.map((raw) => {
        const tc = raw as { id?: string; function?: { name?: string; arguments?: string } };
        const id = tc.id ?? uid('tc');
        const result = toolResults.get(id);
        let parsedResult: unknown;
        try {
          parsedResult = result?.content ? JSON.parse(result.content) : undefined;
        } catch {
          parsedResult = result?.content;
        }
        let parsedArgs: unknown = {};
        try {
          parsedArgs = tc.function?.arguments ? JSON.parse(tc.function.arguments) : {};
        } catch {
          parsedArgs = { _raw: tc.function?.arguments };
        }
        const ok =
          typeof parsedResult === 'object' && parsedResult !== null && 'ok' in parsedResult
            ? Boolean((parsedResult as { ok?: unknown }).ok)
            : true;
        return {
          id,
          name: tc.function?.name ?? 'tool',
          arguments: parsedArgs,
          status: ok ? 'done' : 'error',
          ok,
          result: parsedResult,
        };
      });

      if (row.role === 'assistant' && !row.content && toolCalls.length > 0) {
        const merged = tryMergeAssistant(out, {
          id: row.id,
          serverId: row.id,
          role: 'assistant',
          content: '',
          toolCalls,
          createdAt: row.createdAt,
        });
        if (!merged) {
          out.push({
            id: row.id,
            serverId: row.id,
            role: 'assistant',
            content: '',
            toolCalls,
            createdAt: row.createdAt,
          });
        }
        continue;
      }

      const rawContent = row.content ?? '';
      const parsed =
        row.role === 'assistant' && /<thinking>/i.test(rawContent)
          ? parseThinkingBlocks(rawContent)
          : { thinking: '', answer: stripThinkingArtifacts(rawContent) };

      const next: ChatUiMessage = {
        id: row.id,
        serverId: row.id,
        role: row.role === 'user' ? 'user' : 'assistant',
        content: row.role === 'assistant' ? parsed.answer : rawContent,
        thinking: parsed.thinking || undefined,
        toolCalls,
        createdAt: row.createdAt,
      };

      if (next.role === 'assistant' && tryMergeAssistant(out, next)) {
        continue;
      }
      out.push(next);
    } catch {
      out.push({
        id: row.id || uid('msg'),
        serverId: row.id,
        role: row.role === 'user' ? 'user' : 'assistant',
        content: row.content || '[Message could not be restored]',
        toolCalls: [],
        createdAt: row.createdAt,
        error: 'This message could not be fully restored.',
      });
    }
  }

  return out;
}

function normalizeBubbleText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Fold tool-loop assistant fragments into one bubble when safe. */
function tryMergeAssistant(out: ChatUiMessage[], next: ChatUiMessage): boolean {
  const prev = out[out.length - 1];
  if (!prev || prev.role !== 'assistant' || next.role !== 'assistant') return false;

  const prevNorm = normalizeBubbleText(prev.content);
  const nextNorm = normalizeBubbleText(next.content);
  const sameOrEmpty =
    !prevNorm ||
    !nextNorm ||
    prevNorm === nextNorm ||
    prevNorm.includes(nextNorm) ||
    nextNorm.includes(prevNorm);
  const toolLoop = prev.toolCalls.length > 0 || next.toolCalls.length > 0;
  if (!toolLoop && !sameOrEmpty) return false;

  prev.toolCalls = [...prev.toolCalls, ...next.toolCalls];
  if (next.content) {
    // Prefer the later (usually final) text; drop exact duplicates.
    if (!prev.content || normalizeBubbleText(prev.content) === nextNorm) {
      prev.content = next.content;
    } else if (toolLoop) {
      prev.content = next.content;
    }
  }
  if (next.thinking) {
    prev.thinking = [prev.thinking, next.thinking].filter(Boolean).join('\n\n') || undefined;
  }
  if (next.secureForms?.length) {
    prev.secureForms = [...(prev.secureForms ?? []), ...next.secureForms];
  }
  prev.serverId = next.serverId ?? prev.serverId;
  prev.createdAt = next.createdAt ?? prev.createdAt;
  return true;
}

export function useChat({
  template,
  agentMode,
  conversationId,
  onConversationId,
  onConversationChanged,
}: UseChatOptions = {}) {
  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [canResume, setCanResume] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [lastAgentRun, setLastAgentRun] = useState<string | null>(null);

  const conversationIdRef = useRef<string | undefined>(undefined);
  const hydratedRef = useRef<string | null>(null);
  const messagesRef = useRef<ChatUiMessage[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const titledRef = useRef<Set<string>>(new Set());
  const lastTurnRef = useRef<LastTurn | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const lastEventAtRef = useRef<number>(0);
  const autoRetriedRef = useRef(false);

  messagesRef.current = messages;

  const patchMessage = useCallback((id: string, patch: (m: ChatUiMessage) => ChatUiMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
  }, []);

  const patchToolCall = useCallback(
    (messageId: string, toolCallId: string, patch: Partial<ToolCallState>) => {
      patchMessage(messageId, (m) => ({
        ...m,
        toolCalls: m.toolCalls.map((t) => (t.id === toolCallId ? { ...t, ...patch } : t)),
      }));
    },
    [patchMessage]
  );

  const clearIdleWatch = useCallback(() => {
    if (idleTimerRef.current !== null) {
      window.clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!conversationId) {
      conversationIdRef.current = undefined;
      hydratedRef.current = null;
      setError(null);
      setWarning(null);
      setCanResume(false);
      setActiveTaskId(null);
      setMessages([]);
      setAttachments([]);
      setLoadingHistory(false);
      return () => {
        cancelled = true;
      };
    }

    if (hydratedRef.current === conversationId) return undefined;

    // Live turn already bound this id (stream created the conversation) — don't wipe UI.
    if (
      conversationIdRef.current === conversationId &&
      messagesRef.current.length > 0
    ) {
      hydratedRef.current = conversationId;
      return undefined;
    }

    conversationIdRef.current = conversationId;
    setError(null);
    setWarning(null);
    setCanResume(false);
    setActiveTaskId(null);
    setAttachments([]);
    setMessages([]);
    setLoadingHistory(true);

    void fetchConversation(conversationId)
      .then((res) => {
        if (cancelled) return;
        if (res.ok && res.data) {
          hydratedRef.current = conversationId;
          setMessages(hydrateMessages(res.data.messages ?? []));
          if (res.data.titleLocked || res.data.messageCount > 1) {
            titledRef.current.add(conversationId);
          }
          const unavailable = (res.data.attachments ?? []).filter(
            (a) => a.status === 'unavailable' || a.bytesAvailable === false
          );
          if (unavailable.length > 0) {
            setWarning(
              `${unavailable.length} attachment${unavailable.length === 1 ? '' : 's'} unavailable in storage; chat history still loaded.`
            );
          }
        } else {
          hydratedRef.current = null;
          setMessages([]);
          setError(
            res.error?.message ??
              'Could not load this conversation. Try Reload or start a new chat.'
          );
        }
        setLoadingHistory(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        hydratedRef.current = null;
        setMessages([]);
        setError(err instanceof Error ? err.message : 'Could not load this conversation.');
        setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const reloadHistory = useCallback(() => {
    if (!conversationId) return;
    hydratedRef.current = null;
    conversationIdRef.current = undefined;
    setError(null);
    setWarning(null);
    // Re-trigger the effect by clearing then relying on parent conversationId —
    // force by resetting refs and re-fetching inline.
    conversationIdRef.current = conversationId;
    setLoadingHistory(true);
    setMessages([]);
    void fetchConversation(conversationId)
      .then((res) => {
        if (res.ok && res.data) {
          hydratedRef.current = conversationId;
          setMessages(hydrateMessages(res.data.messages ?? []));
          setError(null);
        } else {
          hydratedRef.current = null;
          setError(res.error?.message ?? 'Could not load this conversation.');
        }
      })
      .catch((err: unknown) => {
        hydratedRef.current = null;
        setError(err instanceof Error ? err.message : 'Could not load this conversation.');
      })
      .finally(() => setLoadingHistory(false));
  }, [conversationId]);

  const addAttachments = useCallback(async (files: File[]) => {
    for (const file of files) {
      const localId = uid('att');
      const previewUrl = IMAGE_TYPES.test(file.type) ? URL.createObjectURL(file) : undefined;
      setAttachments((prev) => [...prev, { localId, file, status: 'uploading', previewUrl }]);
      const result = await uploadChatAttachment(file, conversationIdRef.current);
      setAttachments((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? result.ok && result.data
              ? {
                  ...a,
                  status: 'ready',
                  id: result.data.id,
                  summary: result.data.summary,
                  extractedChars: result.data.extractedChars,
                  serverStatus: result.data.status,
                  learned: result.data.learned ?? null,
                  ocrProvider: result.data.ocrProvider ?? null,
                  documentType: result.data.documentType ?? null,
                  referenceId: result.data.referenceId ?? result.data.id,
                  shortRef: result.data.shortRef ?? null,
                  ocisPath: result.data.ocisPath ?? null,
                  onServer: Boolean(result.data.onServer ?? result.data.ocisPath),
                }
              : { ...a, status: 'error', error: result.error?.message ?? 'Upload failed' }
            : a
        )
      );
    }
  }, []);

  const removeAttachment = useCallback((localId: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  }, []);

  const runTurn = useCallback(
    async (
      history: ChatMessage[],
      attachmentIds: string[],
      options: { truncateAfterMessageId?: string; isResume?: boolean; allowAutoRetry?: boolean } = {}
    ) => {
      lastTurnRef.current = {
        history,
        attachmentIds,
        truncateAfterMessageId: options.truncateAfterMessageId,
      };
      if (!options.isResume) autoRetriedRef.current = false;

      if (options.isResume) {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && (last.error || last.streaming || !last.content)) {
            return prev.slice(0, -1);
          }
          return prev;
        });
      }

      const assistantId = uid('msg');
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          toolCalls: [],
          streaming: true,
          thinking: '',
          thinkingStreaming: false,
        },
      ]);
      setSending(true);
      setError(null);
      setWarning(null);
      setCanResume(false);

      const controller = new AbortController();
      abortRef.current = controller;
      lastEventAtRef.current = Date.now();
      clearIdleWatch();
      let stalled = false;
      idleTimerRef.current = window.setInterval(() => {
        if (Date.now() - lastEventAtRef.current > STREAM_IDLE_MS) {
          stalled = true;
          clearIdleWatch();
          controller.abort();
        }
      }, 5000);

      const touch = () => {
        lastEventAtRef.current = Date.now();
      };

      let sawDone = false;
      // True once we have any stream payload worth keeping (don't auto-retry then).
      let sawProgress = false;
      // After tools finish, the next model tokens are a new answer — replace
      // prior tool-turn narration instead of appending (avoids duplicate prose).
      let replaceContentOnNextToken = false;

      const handleEvent = (event: AgentChatStreamEvent) => {
        touch();
        switch (event.type) {
          case 'agent_task':
            sawProgress = true;
            setActiveTaskId(event.taskId);
            patchMessage(assistantId, (m) => ({
              ...m,
              agentRun: { taskId: event.taskId, status: event.status, goal: event.goal },
            }));
            break;
          case 'agent_plan':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              agentRun: {
                ...(m.agentRun ?? { taskId: event.taskId, status: 'planning' }),
                planSteps: (event.plan ?? []).map((p) => p.type),
              },
            }));
            break;
          case 'agent_subtasks':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              agentRun: {
                ...(m.agentRun ?? { taskId: event.taskId, status: 'running' }),
                subtaskCount: (event.subtasks ?? []).length,
              },
            }));
            break;
          case 'agent_done':
            sawProgress = true;
            setLastAgentRun(event.taskId);
            patchMessage(assistantId, (m) => ({
              ...m,
              agentRun: {
                ...(m.agentRun ?? { taskId: event.taskId, status: 'completed' }),
                status: 'completed',
                summary: event.summary,
              },
            }));
            break;
          case 'turn':
            conversationIdRef.current = event.conversationId;
            onConversationId?.(event.conversationId);
            if (event.userMessageId) {
              setMessages((prev) => {
                const index = prev.map((m) => m.role).lastIndexOf('user');
                if (index === -1) return prev;
                const next = [...prev];
                next[index] = { ...next[index], serverId: event.userMessageId! };
                return next;
              });
            }
            break;
          case 'start':
            conversationIdRef.current = event.conversationId;
            onConversationId?.(event.conversationId);
            break;
          case 'thinking_start':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              thinkingStreaming: true,
              thinking: m.thinking || '',
            }));
            break;
          case 'thinking':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              thinkingStreaming: true,
              // Stream-safe: do not trim mid-chunk (would glue words together).
              thinking: stripThinkingArtifacts((m.thinking || '') + event.delta),
            }));
            break;
          case 'thinking_done':
            patchMessage(assistantId, (m) => ({
              ...m,
              thinkingStreaming: false,
              thinking: m.thinking
                ? stripThinkingArtifacts(m.thinking, { trim: true })
                : m.thinking,
            }));
            break;
          case 'token': {
            sawProgress = true;
            const delta = event.delta;
            const startFresh = replaceContentOnNextToken;
            if (startFresh) replaceContentOnNextToken = false;
            const prior =
              messagesRef.current.find((m) => m.id === assistantId)?.content ?? '';
            const combinedPreview = startFresh ? delta : prior + delta;
            if (looksLikeHtmlErrorPage(combinedPreview)) {
              const safe = summarizeHttpErrorBody(502, combinedPreview);
              setError(safe);
              setCanResume(true);
              patchMessage(assistantId, (m) => ({
                ...m,
                content: '',
                streaming: false,
                error: safe,
              }));
              break;
            }
            patchMessage(assistantId, (m) => {
              const combined = startFresh ? delta : m.content + delta;
              if (/<\/?thinking>/i.test(combined)) {
                // Stream-safe: keep trailing spaces between token deltas.
                const merged = mergeThinkingContent(combined, m.thinking, { trim: false });
                return {
                  ...m,
                  content: merged.content,
                  thinking: merged.thinking,
                  thinkingStreaming:
                    /<thinking>/i.test(combined) && !/<\/thinking>/i.test(combined),
                };
              }
              // Stream-safe strip (no trim) so spaces between token deltas survive.
              const cleaned = stripThinkingArtifacts(combined);
              return { ...m, content: cleaned || combined };
            });
            break;
          }
          case 'tool_call':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              toolCalls: [
                ...m.toolCalls,
                { id: event.id, name: event.name, arguments: event.arguments, status: 'running' },
              ],
            }));
            break;
          case 'tool_result':
            sawProgress = true;
            replaceContentOnNextToken = true;
            patchToolCall(assistantId, event.id, {
              status: event.ok ? 'done' : 'error',
              ok: event.ok,
              result: event.result,
            });
            break;
          case 'secure_form':
            sawProgress = true;
            patchMessage(assistantId, (m) => ({
              ...m,
              secureForms: [
                ...(m.secureForms ?? []),
                {
                  id: `sf-${event.formId}`,
                  formId: event.formId,
                  title: event.title,
                  fields: (event.fields ?? []) as SecureFormFieldDef[],
                  irsFormNumber: event.irsFormNumber,
                  fieldMapping: event.fieldMapping,
                  shareUrl: event.shareUrl,
                  recipientName: event.recipientName,
                  expiresAt: event.expiresAt,
                  purpose: event.purpose,
                  toolCallId: event.id,
                  status: event.shareUrl ? 'awaiting_recipient' : 'awaiting_user_input',
                },
              ],
            }));
            break;
          case 'approval_required':
            sawProgress = true;
            replaceContentOnNextToken = true;
            patchToolCall(assistantId, event.id, {
              status: 'approval_required',
              ok: false,
              result: event.result,
              approvalId: event.approvalId,
              approvalStatus: event.approvalStatus,
              riskLevel: event.riskLevel,
            });
            break;
          case 'warning':
            setWarning(event.message);
            break;
          case 'ping':
            // Keepalive — touch() already refreshed the idle watchdog.
            break;
          case 'done': {
            sawDone = true;
            sawProgress = true;
            conversationIdRef.current = event.conversationId;
            onConversationId?.(event.conversationId);
            const thinkingFromEvent =
              'thinking' in event && typeof (event as { thinking?: string }).thinking === 'string'
                ? (event as { thinking?: string }).thinking
                : undefined;
            patchMessage(assistantId, (m) => {
              const raw = event.content || m.content;
              const merged = mergeThinkingContent(raw, thinkingFromEvent || m.thinking);
              return {
                ...m,
                content: stripThinkingArtifacts(merged.content, { trim: true }),
                thinking: merged.thinking
                  ? stripThinkingArtifacts(merged.thinking, { trim: true })
                  : undefined,
                streaming: false,
                thinkingStreaming: false,
                error: undefined,
              };
            });
            break;
          }
          case 'error': {
            const safe = summarizeHttpErrorBody(0, event.message || 'Chat error');
            setWarning(null);
            setError(safe);
            setCanResume(true);
            patchMessage(assistantId, (m) =>
              failRunningTools({ ...m, streaming: false, error: safe }, safe)
            );
            break;
          }
        }
      };

      let transportFailed = false;
      let streamOpened = false;
      try {
        const openStream = agentMode ? openAgentChatStream : openAiChatStream;
        const response = await openStream(
          {
            messages: history,
            template: template || undefined,
            attachment_ids: attachmentIds.length ? attachmentIds : undefined,
            conversation_id: conversationIdRef.current,
            use_stored_history: true,
            truncate_after_message_id: options.truncateAfterMessageId,
          },
          controller.signal,
          {
            onRetry: () => {
              setError('Retrying…');
              setCanResume(false);
            },
          }
        );
        streamOpened = true;
        // Clear transient retry banner once the stream is open.
        setError(null);
        await consumeChatStream(response, handleEvent);
        if (!sawDone) {
          const earlyMsg = stalled
            ? 'Stream stalled. Use Resume to continue.'
            : 'Connection closed early. You can Resume.';
          patchMessage(assistantId, (m) => {
            if (!m.streaming && !m.toolCalls.some((t) => t.status === 'running')) return m;
            const merged = mergeThinkingContent(m.content, m.thinking);
            return failRunningTools(
              {
                ...m,
                content: merged.content,
                thinking: merged.thinking,
                streaming: false,
                thinkingStreaming: false,
                error: m.content ? undefined : earlyMsg,
              },
              earlyMsg
            );
          });
          if (!controller.signal.aborted || stalled) {
            setError(earlyMsg);
            setCanResume(true);
            transportFailed = true;
          }
        }
      } catch (err) {
        const aborted = err instanceof DOMException && err.name === 'AbortError';
        if (stalled) {
          const msg = 'Stream stalled. Use Resume to continue.';
          setError(msg);
          setCanResume(true);
          transportFailed = true;
          patchMessage(assistantId, (m) =>
            failRunningTools(
              {
                ...m,
                streaming: false,
                thinkingStreaming: false,
                error: 'Stream stalled',
              },
              msg
            )
          );
        } else if (aborted) {
          patchMessage(assistantId, (m) =>
            failRunningTools(
              {
                ...m,
                streaming: false,
                thinkingStreaming: false,
              },
              'Stopped'
            )
          );
        } else {
          const message = transportErrorMessage(err, streamOpened === false);
          setError(message);
          setCanResume(true);
          transportFailed = true;
          patchMessage(assistantId, (m) =>
            failRunningTools(
              {
                ...m,
                streaming: false,
                thinkingStreaming: false,
                error: message,
              },
              message
            )
          );
        }
      } finally {
        clearIdleWatch();
        setSending(false);
        abortRef.current = null;

        const convo = conversationIdRef.current;
        if (convo && !titledRef.current.has(convo)) {
          titledRef.current.add(convo);
          void generateConversationTitle(convo).then(() => onConversationChanged?.());
        } else {
          onConversationChanged?.();
        }
      }

      // Stream died before any tokens: auto-retry once. Pre-stream 502/503 already
      // used exponential backoff inside open*ChatStream — Resume is the fallback.
      if (
        transportFailed &&
        streamOpened &&
        !sawProgress &&
        !options.isResume &&
        options.allowAutoRetry !== false &&
        !autoRetriedRef.current &&
        lastTurnRef.current
      ) {
        autoRetriedRef.current = true;
        setError('Retrying…');
        setCanResume(false);
        await runTurn(history, attachmentIds, {
          ...options,
          isResume: true,
          allowAutoRetry: false,
        });
      }
    },
    [
      agentMode,
      clearIdleWatch,
      onConversationChanged,
      onConversationId,
      patchMessage,
      patchToolCall,
      template,
    ]
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      const ready = attachments.filter((a) => a.status === 'ready' && a.id);
      const attachmentRefs: MessageAttachmentRef[] = ready.map((a) => ({
        id: a.id!,
        filename: a.file.name,
        contentType: a.file.type,
        sizeBytes: a.file.size,
        extractedChars: a.extractedChars,
        status: a.serverStatus,
        referenceId: a.referenceId ?? a.id,
        shortRef: a.shortRef,
        ocisPath: a.ocisPath,
        onServer: a.onServer,
      }));

      const userMessage: ChatUiMessage = {
        id: uid('msg'),
        role: 'user',
        content: trimmed,
        toolCalls: [],
        attachments: attachmentRefs.length ? attachmentRefs : undefined,
      };

      setMessages((prev) => [...prev, userMessage]);
      for (const a of attachments) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      setAttachments([]);

      await runTurn([{ role: 'user', content: trimmed }], ready.map((a) => a.id!));
    },
    [attachments, runTurn, sending]
  );

  const regenerate = useCallback(async () => {
    if (sending) return;
    const lastUserIndex = [...messages].reverse().findIndex((m) => m.role === 'user');
    if (lastUserIndex === -1) return;
    const index = messages.length - 1 - lastUserIndex;
    const userMessage = messages[index];

    setMessages(messages.slice(0, index + 1));
    await runTurn([{ role: 'user', content: userMessage.content }], [], {
      truncateAfterMessageId: userMessage.serverId,
    });
  }, [messages, runTurn, sending]);

  const resume = useCallback(async () => {
    if (sending || !lastTurnRef.current) return;
    const turn = lastTurnRef.current;
    // Truncate at the user message that started this turn so Resume does not
    // duplicate the user row or leave partial tool rows that would double-run.
    let truncateId = turn.truncateAfterMessageId;
    if (!truncateId) {
      const lastUser = [...messagesRef.current].reverse().find((m) => m.role === 'user');
      truncateId = lastUser?.serverId;
    }
    setCanResume(false);
    setError(null);
    await runTurn(turn.history, turn.attachmentIds, {
      truncateAfterMessageId: truncateId,
      isResume: true,
      allowAutoRetry: false,
    });
  }, [runTurn, sending]);

  const editAndResend = useCallback(
    async (messageId: string, newText: string) => {
      if (sending) return;
      const index = messages.findIndex((m) => m.id === messageId);
      if (index === -1) return;
      const trimmed = newText.trim();
      if (!trimmed) return;

      const original = messages[index];
      setMessages([
        ...messages.slice(0, index),
        { ...original, content: trimmed, serverId: undefined },
      ]);
      await runTurn([{ role: 'user', content: trimmed }], [], {
        truncateAfterMessageId: original.serverId,
      });
    },
    [messages, runTurn, sending]
  );

  const decideApproval = useCallback(
    async (toolCall: ToolCallState, approve: boolean) => {
      if (!toolCall.approvalId) return;
      const result = approve
        ? await approveApproval(toolCall.approvalId)
        : await rejectApproval(toolCall.approvalId);
      if (!result.ok) return;

      setMessages((prev) =>
        prev.map((m) => ({
          ...m,
          toolCalls: m.toolCalls.map((t) =>
            t.id === toolCall.id ? { ...t, approvalStatus: approve ? 'approved' : 'rejected' } : t
          ),
        }))
      );

      if (approve) {
        const resumeText =
          `Approval ${toolCall.approvalId} for "${toolCall.name}" has been approved. ` +
          `Please proceed with that action, passing approval_id "${toolCall.approvalId}".`;
        setMessages((prev) => [
          ...prev,
          { id: uid('msg'), role: 'user', content: resumeText, toolCalls: [] },
        ]);
        await runTurn([{ role: 'user', content: resumeText }], []);
      }
    },
    [runTurn]
  );

  const stop = useCallback(() => {
    clearIdleWatch();
    abortRef.current?.abort();
  }, [clearIdleWatch]);

  return {
    messages,
    attachments,
    sending,
    loadingHistory,
    error,
    warning,
    canResume,
    send,
    stop,
    resume,
    regenerate,
    editAndResend,
    addAttachments,
    removeAttachment,
    decideApproval,
    reloadHistory,
    clearWarning: () => setWarning(null),
    conversationId: conversationIdRef.current,
    activeTaskId,
    lastAgentRun,
  };
}
